import { memo, useEffect, useRef } from "react";
import { useTranslate } from "helpers";
import { WarehouseAddressCardProps } from "./types";
import { Tabs } from "ui/components/Tabs";
import { NoResults } from "ui/components/NoResults";
import { Map, MapRef, Marker } from "react-map-gl/mapbox";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const WarehouseAddressCard: React.FC<WarehouseAddressCardProps> = (props) => {
  const { fleetWarehouseDetail } = props;

  const { t } = useTranslate();

  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.resize();
    });
    resizeObserver.observe(mapContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const hasAddress =
    !!fleetWarehouseDetail.addressCountry ||
    !!fleetWarehouseDetail.addressCity ||
    !!fleetWarehouseDetail.addressLine1;

  const latitude = fleetWarehouseDetail.addressLatitude;
  const longitude = fleetWarehouseDetail.addressLongitude;
  const hasCoords = latitude !== null && longitude !== null && latitude !== undefined && longitude !== undefined;

  const formattedAddress = [
    fleetWarehouseDetail.addressLine1,
    fleetWarehouseDetail.addressLine2,
    fleetWarehouseDetail.addressCity,
    fleetWarehouseDetail.addressPostalCode,
    fleetWarehouseDetail.addressState,
    fleetWarehouseDetail.addressCountry,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Tabs
      tabs={[
        {
          label: t("admin.contact.address.tab.map"),
          content: hasCoords ? (
            <div ref={mapContainerRef} className="relative w-full h-[500px] mx-[-1px]">
              <Map
                ref={mapRef}
                mapboxAccessToken={MAPBOX_TOKEN}
                initialViewState={{
                  latitude: latitude!,
                  longitude: longitude!,
                  zoom: 14,
                }}
                mapStyle="mapbox://styles/mapbox/streets-v11"
                style={{ width: "100%", height: "100%" }}
              >
                <Marker latitude={latitude!} longitude={longitude!}>
                  <div className="text-2xl">📍</div>
                </Marker>
              </Map>
            </div>
          ) : (
            <div className="h-full w-full min-h-[300px] flex items-center justify-center">
              <NoResults message={t("admin.contact.address.map.nocoordinates")} />
            </div>
          ),
        },
        {
          label: t("admin.contact.address.tab.view"),
          content: (
            <div className="mx-[-1px]">
              {!hasAddress ? (
                <NoResults message={t("admin.contact.address.view.noresults")} />
              ) : (
                <div className="rounded-sm bg-white ">

                  <div className="grid grid-cols-3 gap-3 p-4">
                    <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm min-h-[70px]">
                      <div className="text-sm text-slate-600">
                        {t("admin.fleet.warehouse.detail.address.country")}
                      </div>
                      <div className="text-md font-medium text-slate-800 truncate">
                        {fleetWarehouseDetail.addressCountry}
                      </div>
                    </div>
                    <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm min-h-[70px]">
                      <div className="text-sm text-slate-600">
                        {t("admin.fleet.warehouse.detail.address.state")}
                      </div>
                      <div className="text-md font-medium text-slate-800 truncate">
                        {fleetWarehouseDetail.addressState}
                      </div>
                    </div>
                    <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm min-h-[70px]">
                      <div className="text-sm text-slate-600">
                        {t("admin.fleet.warehouse.detail.address.city")}
                      </div>
                      <div className="text-md font-medium text-slate-800 truncate">
                        {fleetWarehouseDetail.addressCity}
                      </div>
                    </div>
                    <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm min-h-[70px]">
                      <div className="text-sm text-slate-600">
                        {t("admin.fleet.warehouse.detail.address.postalcode")}
                      </div>
                      <div className="text-md font-medium text-slate-800 truncate">
                        {fleetWarehouseDetail.addressPostalCode}
                      </div>
                    </div>
                    <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm min-h-[70px]">
                      <div className="text-sm text-slate-600">
                        {t("admin.fleet.warehouse.detail.address.addressline1")}
                      </div>
                      <div className="text-md font-medium text-slate-800 truncate">
                        {fleetWarehouseDetail.addressLine1}
                      </div>
                    </div>
                    <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm min-h-[70px]">
                      <div className="text-sm text-slate-600">
                        {t("admin.fleet.warehouse.detail.address.addressline2")}
                      </div>
                      <div className="text-md font-medium text-slate-800 truncate">
                        {fleetWarehouseDetail.addressLine2}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default memo(WarehouseAddressCard);
